import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { 
  Button, Input, Label, Textarea
} from '../ui/UIComponents';
import { Permission, PermissionFormData, Application } from '../../types/rbac.types';
import { createPermission, updatePermission, getApplication } from '../../services/rbacApi';

interface PermissionFormProps {
  isOpen: boolean;
  onClose: (refreshData?: boolean) => void;
  permission: Permission | null;
  applicationId: string;
}

// Default categories if application doesn't have any defined
const DEFAULT_PERMISSION_CATEGORIES = [
  { value: 'access', label: 'Access' },
  { value: 'read', label: 'Read' },
  { value: 'write', label: 'Write' },
  { value: 'delete', label: 'Delete' },
  { value: 'admin', label: 'Admin' },
  { value: 'system', label: 'System' }
];

const PermissionForm: React.FC<PermissionFormProps> = ({ isOpen, onClose, permission, applicationId }) => {
  const [formData, setFormData] = useState<PermissionFormData>({
    name: '',
    description: '',
    code: '',
    category: 'access',
    application_id: applicationId
  });
  const [application, setApplication] = useState<Application | null>(null);
  const [permissionCategories, setPermissionCategories] = useState<Array<{value: string, label: string}>>(DEFAULT_PERMISSION_CATEGORIES);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Fetch application data
  useEffect(() => {
    const fetchApplication = async () => {
      try {
        const appData = await getApplication(applicationId);
        setApplication(appData);
        
        // If application has permission categories, use them
        if (appData.permission_categories && appData.permission_categories.length > 0) {
          const categories = appData.permission_categories.map(category => ({
            value: category,
            label: category.charAt(0).toUpperCase() + category.slice(1)
          }));
          setPermissionCategories(categories);
        }
      } catch (err) {
        console.error('Error fetching application:', err);
      }
    };

    if (applicationId) {
      fetchApplication();
    }
  }, [applicationId]);

  // Initialize form with permission data if editing
  useEffect(() => {
    if (permission) {
      setFormData({
        name: permission.name,
        description: permission.description || '',
        code: permission.code,
        category: permission.category,
        application_id: permission.application_id
      });
    } else {
      setFormData({
        name: '',
        description: '',
        code: '',
        category: 'access',
        application_id: applicationId
      });
    }
  }, [permission, applicationId, isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error for the field being edited
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Generate code from name
  const generateCode = () => {
    if (formData.name && application) {
      // Use application name instead of ID for the prefix
      const appPrefix = application.name
        .split(' ')[0]  // Take first word of app name
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '')
        .substring(0, 3);  // Limit to 3 chars
      
      const nameCode = formData.name
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '_')
        .replace(/_+/g, '_');
      
      const code = `${appPrefix}_${nameCode}`;
      setFormData(prev => ({ ...prev, code }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.code.trim()) {
      newErrors.code = 'Code is required';
    } else if (!/^[A-Z0-9_]+$/.test(formData.code)) {
      newErrors.code = 'Code must contain only uppercase letters, numbers, and underscores';
    }
    
    if (!formData.category.trim()) {
      newErrors.category = 'Category is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      // Submitting permission form data
      
      if (permission) {
        // Update existing permission
        await updatePermission(permission.id, formData);
      } else {
        // Create new permission
        await createPermission(formData);
      }
      
      onClose(true); // Close form and refresh data
    } catch (err: any) {
      console.error('Error saving permission:', err);
      setSubmitError(err.response?.data?.error || 'Failed to save permission');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div 
      className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={handleBackdropClick}
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
    >
      <div 
        className="bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700 bg-gray-800/50">
          <h2 className="text-xl font-semibold text-white">
            {permission ? 'Edit Permission' : 'Create New Permission'}
          </h2>
          <button
            onClick={() => onClose()}
            className="text-gray-400 hover:text-white transition-colors p-1"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto px-6 py-6">
            {submitError && (
              <div className="mb-6 p-4 bg-red-900/30 border border-red-500 text-red-300 rounded-lg">
                {submitError}
              </div>
            )}
            
            <div className="grid grid-cols-1 gap-6">
              <div>
                <Label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                  Name *
                </Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter permission name"
                  className={`w-full px-4 py-3 bg-gray-800 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.name ? 'border-red-500' : 'border-gray-600'}`}
                />
                {errors.name && <p className="mt-2 text-sm text-red-400">{errors.name}</p>}
              </div>
              
              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label htmlFor="code" className="block text-sm font-medium text-gray-300">
                    Permission Code *
                  </Label>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="xs"
                    onClick={generateCode}
                    disabled={!formData.name || !application}
                    className="px-3 py-1 text-xs border border-gray-600 text-gray-300 hover:bg-gray-700 rounded transition-colors"
                  >
                    Generate
                  </Button>
                </div>
                <Input
                  id="code"
                  name="code"
                  value={formData.code}
                  onChange={handleChange}
                  placeholder="APP_PERMISSION_CODE"
                  className={`w-full px-4 py-3 bg-gray-800 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.code ? 'border-red-500' : 'border-gray-600'}`}
                  disabled={!!permission}
                />
                {errors.code && <p className="mt-2 text-sm text-red-400">{errors.code}</p>}
                <p className="mt-2 text-xs text-gray-500">
                  Unique identifier for the permission (uppercase letters, numbers, and underscores only)
                </p>
              </div>
              
              <div>
                <Label htmlFor="category" className="block text-sm font-medium text-gray-300 mb-2">
                  Category *
                </Label>
                <select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 bg-gray-800 border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.category ? 'border-red-500' : 'border-gray-600'}`}
                >
                  {permissionCategories.map(category => (
                    <option key={category.value} value={category.value}>
                      {category.label}
                    </option>
                  ))}
                </select>
                {errors.category && <p className="mt-2 text-sm text-red-400">{errors.category}</p>}
              </div>
              
              <div>
                <Label htmlFor="description" className="block text-sm font-medium text-gray-300 mb-2">
                  Description
                </Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Enter permission description"
                  rows={4}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <input type="hidden" name="application_id" value={applicationId} />
            </div>
          </div>
          
          {/* Footer with buttons */}
          <div className="flex justify-end gap-4 px-6 py-4 border-t border-gray-700 bg-gray-800/30">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onClose()}
              disabled={isSubmitting}
              className="px-6 py-2 border border-gray-600 text-gray-300 hover:bg-gray-700 rounded-lg transition-colors"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={isSubmitting}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              {isSubmitting ? 'Saving...' : permission ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );

  // Use portal to render modal at document body level
  return createPortal(modalContent, document.body);
};

export default PermissionForm; 